import { QueryClient, UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@api/ApiEndpoints';
import { useHttpClient } from '@hooks/utils/httpConfig';
import { useFeatureFlags, useFeatureFlagsState } from '@hooks/utils/dashboard/features/useFeatureFlags';
import {
  ExtendedTimeSeriesDto,
  GroupedSummaryDto,
  ResourceSummaryDto,
  SummaryPayloadDto,
} from './types';

const SUMMARY_STALE_TIME = 2 * 60 * 1000; // 2 minutes � summaries are pre-computed

const SUMMARY_API_OVERRIDE_KEY = 'summaryApiOverride';
const FRESHNESS_INDICATOR_OVERRIDE_KEY = 'freshnessIndicatorOverride';

const SUMMARY_RESOURCES = {
  utilisation: ['Subscriptions', 'Licenses', 'Devices', 'SharePointUsage', 'SharePointSites', 'TeamsHealth', 'UserAssociatedTeams'],
  adoption: ['UserActivity', 'SharedMailboxes', 'Persons', 'TeamsHealth', 'TeamsAdoption', 'Devices', 'Rooms', 'RoomUsage', 'GroupActivity'],
  security: [
    'Persons',
    'SecureScores',
    'SharedMailboxes',
    'UserAssociatedTeams',
    'MailboxDelegationAccesses',
    'SuspiciousAccountEvents',
    'ExternalApplications',
    'CountryLoginAttempts',
    'LoginAttemptTypes',
    'UserAccountSecurityInfo',
    'MailboxForwardingRules',
    'OutdatedBrowsers',
    'OutdatedOperatingSystems',
  ],
  productivity: ['UserActivity'],
} as const;

// Capture overrides at module load time so they are persisted even when the
// param appears on a redirect/impersonation URL before any hook renders.
((): void => {
  const params = new URLSearchParams(window.location.search);
  const isCompareChild = params.get('compareChild') === 'true' || window.name === 'compare-summary' || window.name === 'compare-legacy';

  const summaryParam = params.get('summaryApi');
  if (params.get('compareChild') === 'true' && (summaryParam === 'true' || summaryParam === 'false')) {
    window.name = summaryParam === 'true' ? 'compare-summary' : 'compare-legacy';
  }

  if (!isCompareChild && (summaryParam === 'true' || summaryParam === 'false')) {
    sessionStorage.setItem(SUMMARY_API_OVERRIDE_KEY, summaryParam);
  } else if (!isCompareChild && summaryParam === 'clear') {
    sessionStorage.removeItem(SUMMARY_API_OVERRIDE_KEY);
  }

  const freshnessParam = params.get('freshnessIndicator');
  if (freshnessParam === 'true' || freshnessParam === 'false') {
    sessionStorage.setItem(FRESHNESS_INDICATOR_OVERRIDE_KEY, freshnessParam);
  } else if (freshnessParam === 'clear') {
    sessionStorage.removeItem(FRESHNESS_INDICATOR_OVERRIDE_KEY);
  }
})();

/**
 * Returns true when the summary-apis feature flag is enabled.
 * Can be overridden via query parameter for validation:
 *   ?summaryApi=true  → force enable (remembered for this tab)
 *   ?summaryApi=false → force disable (remembered for this tab)
 *   ?summaryApi=clear → remove the override and revert to the feature flag
 * The override is stored in sessionStorage so it persists across navigation
 * within the same tab without needing the query parameter on every page.
 */
export function useSummaryApisEnabled(): boolean {
  const flags = useFeatureFlags();
  const params = new URLSearchParams(window.location.search);
  const summaryParam = params.get('summaryApi');
  const isCompareChild = params.get('compareChild') === 'true';

  if (isCompareChild && (summaryParam === 'true' || summaryParam === 'false')) {
    window.name = summaryParam === 'true' ? 'compare-summary' : 'compare-legacy';
    return summaryParam === 'true';
  }

  if (window.name === 'compare-summary') return true;
  if (window.name === 'compare-legacy') return false;

  const stored = sessionStorage.getItem(SUMMARY_API_OVERRIDE_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return flags['summary-apis'] === true;
}

/**
 * Returns true once summary-vs-legacy source selection is resolved for this tab.
 * This avoids firing legacy queries before feature flags have loaded.
 */
export function useSummaryApisSelectionResolved(): boolean {
  const flagsState = useFeatureFlagsState();
  const params = new URLSearchParams(window.location.search);
  const summaryParam = params.get('summaryApi');
  const isCompareChild = params.get('compareChild') === 'true';

  if (isCompareChild && (summaryParam === 'true' || summaryParam === 'false')) {
    return true;
  }

  if (window.name === 'compare-summary' || window.name === 'compare-legacy') {
    return true;
  }

  const stored = sessionStorage.getItem(SUMMARY_API_OVERRIDE_KEY);
  if (stored === 'true' || stored === 'false') {
    return true;
  }

  return flagsState.isResolved;
}

/**
 * Sets or clears the summary API override for the current browser tab.
 */
export function setSummaryApisOverride(enabled: boolean | null): void {
  if (enabled === null) {
    sessionStorage.removeItem(SUMMARY_API_OVERRIDE_KEY);
    return;
  }

  sessionStorage.setItem(SUMMARY_API_OVERRIDE_KEY, enabled ? 'true' : 'false');
}

/**
 * Returns true when the freshness-indicator feature flag is enabled.
 * Only visible when summary-apis is also on.
 * Can be overridden via query parameter for validation:
 *   ?freshnessIndicator=true  → force enable (remembered for this tab)
 *   ?freshnessIndicator=false → force disable (remembered for this tab)
 *   ?freshnessIndicator=clear → remove the override and revert to the feature flag
 */
export function useFreshnessIndicatorEnabled(): boolean {
  const flags = useFeatureFlags();
  const summaryEnabled = useSummaryApisEnabled();
  const stored = sessionStorage.getItem(FRESHNESS_INDICATOR_OVERRIDE_KEY);
  if (stored === 'true') return summaryEnabled;
  if (stored === 'false') return false;
  return summaryEnabled && flags['freshness-indicator'] === true;
}

export function useUtilisationSummaryQuery(): UseQueryResult<GroupedSummaryDto> {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const enabled = useSummaryApisEnabled();

  return useQuery({
    queryKey: ['summary', 'utilisation'],
    queryFn: async () => fetchSummaryByResources(queryClient, httpClient, SUMMARY_RESOURCES.utilisation),
    staleTime: SUMMARY_STALE_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  });
}

export function useAdoptionSummaryQuery(): UseQueryResult<GroupedSummaryDto> {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const enabled = useSummaryApisEnabled();

  return useQuery({
    queryKey: ['summary', 'adoption'],
    queryFn: async () => fetchSummaryByResources(queryClient, httpClient, SUMMARY_RESOURCES.adoption),
    staleTime: SUMMARY_STALE_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  });
}

export function useSecuritySummaryQuery(): UseQueryResult<GroupedSummaryDto> {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const enabled = useSummaryApisEnabled();

  return useQuery({
    queryKey: ['summary', 'security'],
    queryFn: async () => fetchSummaryByResources(queryClient, httpClient, SUMMARY_RESOURCES.security),
    staleTime: SUMMARY_STALE_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  });
}

export function useProductivitySummaryQuery(): UseQueryResult<GroupedSummaryDto> {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const enabled = useSummaryApisEnabled();

  return useQuery({
    queryKey: ['summary', 'productivity'],
    queryFn: async () => fetchSummaryByResources(queryClient, httpClient, SUMMARY_RESOURCES.productivity),
    staleTime: SUMMARY_STALE_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled,
  });
}

function toObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function getCaseInsensitive(record: Record<string, unknown>, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  const match = Object.keys(record).find((k) => k.toLowerCase() === key.toLowerCase());
  return match ? record[match] : undefined;
}

function normalizeTimeSeries(rawTimeSeries: unknown): ResourceSummaryDto['timeSeries'] {
  const input = toObject(rawTimeSeries);
  const output: ResourceSummaryDto['timeSeries'] = {};

  for (const [seriesKey, rawPoints] of Object.entries(input)) {
    if (!Array.isArray(rawPoints)) continue;

    const normalizedPoints = rawPoints
      .map((rawPoint) => {
        const point = toObject(rawPoint);
        const timestampValue = getCaseInsensitive(point, 'timestamp');
        const valueValue = getCaseInsensitive(point, 'value');

        if (typeof timestampValue !== 'string') return undefined;

        const numericValue = Number(valueValue ?? Number.NaN);
        if (!Number.isFinite(numericValue)) return undefined;

        return {
          timestamp: timestampValue,
          value: numericValue,
        };
      })
      .filter((point): point is { timestamp: string; value: number } => !!point);

    output[seriesKey] = normalizedPoints;
  }

  return output;
}

function toCanonicalResourceTypeKey(resourceTypeKey: string): string {
  if (!resourceTypeKey) return resourceTypeKey;
  return resourceTypeKey[0].toUpperCase() + resourceTypeKey.slice(1);
}

function toResourceSummary(summaryPayload: unknown, fallbackResourceType: string): ResourceSummaryDto | undefined {
  const summary = toObject(summaryPayload);
  if (Object.keys(summary).length === 0) return undefined;

  const resourceTypeFromPayload = getCaseInsensitive(summary, 'resourceType');
  const resourceType = typeof resourceTypeFromPayload === 'string' && resourceTypeFromPayload.length > 0
    ? resourceTypeFromPayload
    : toCanonicalResourceTypeKey(fallbackResourceType);
  const totalCount = Number(getCaseInsensitive(summary, 'totalCount') ?? 0);
  const lastUpdatedAtValue = getCaseInsensitive(summary, 'lastUpdatedAt');
  const lastUpdatedAt = typeof lastUpdatedAtValue === 'string' ? lastUpdatedAtValue : '';

  return {
    resourceType,
    totalCount,
    lastUpdatedAt,
    countsByFilter: toObject(getCaseInsensitive(summary, 'countsByFilter')) as Record<string, number>,
    aggregates: toObject(getCaseInsensitive(summary, 'aggregates')),
    hierarchies: toObject(getCaseInsensitive(summary, 'hierarchies')),
    previousValues: toObject(getCaseInsensitive(summary, 'previousValues')) as Record<string, number>,
    percentageChanges: toObject(getCaseInsensitive(summary, 'percentageChanges')) as Record<string, number>,
    timeSeries: normalizeTimeSeries(getCaseInsensitive(summary, 'timeSeries')),
  };
}

function normalizeSummaryDocuments(documents: unknown): GroupedSummaryDto {
  const root = toObject(documents);
  const summaries: Record<string, ResourceSummaryDto> = {};

  for (const [resourceTypeKey, rawDoc] of Object.entries(root)) {
    const summary = toResourceSummary(rawDoc as SummaryPayloadDto, resourceTypeKey);
    if (!summary) continue;
    summaries[summary.resourceType] = summary;
  }

  return { summaries };
}

async function fetchSummaryByResources(
  queryClient: QueryClient,
  httpClient: { get<T>(url: string, params?: unknown): Promise<T> },
  resourceTypes: readonly string[],
): Promise<GroupedSummaryDto> {
  const resources = await Promise.all(
    resourceTypes.map(async (resourceType) => {
      const summary = await queryClient.fetchQuery({
        queryKey: ['summary', 'resource', resourceType],
        queryFn: () => httpClient.get<SummaryPayloadDto>(API_ENDPOINTS.SUMMARY_RESOURCE(resourceType)),
        staleTime: SUMMARY_STALE_TIME,
      });

      return [resourceType, summary] as const;
    }),
  );

  const groupedPayload = Object.fromEntries(resources);
  return normalizeSummaryDocuments(groupedPayload);
}

/**
 * Fetches extended time series (60 or 90 days) for a summary page.
 *
 * Only fires when `enabled` is true — call this when the user selects a 60 or 90 day range.
 * The 7 and 30 day views are served from the grouped summary hooks above and do not need this.
 *
 * The response contains only time series data. Merge it into an existing GroupedSummaryDto
 * by replacing `summary.timeSeries` with the extended series for the relevant resource type.
 */
export function useExtendedTimeSeriesQuery(
  page: 'utilisation' | 'adoption' | 'security' | 'productivity',
  days: 60 | 90,
  enabled: boolean,
): UseQueryResult<ExtendedTimeSeriesDto> {
  const httpClient = useHttpClient();
  const summaryEnabled = useSummaryApisEnabled();

  return useQuery({
    queryKey: ['summary', page, 'timeseries', days],
    queryFn: () => httpClient.get<ExtendedTimeSeriesDto>(API_ENDPOINTS.SUMMARY_EXTENDED_TIMESERIES(page, days)),
    staleTime: SUMMARY_STALE_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: summaryEnabled && enabled,
  });
}

/**
 * Helper to extract a single resource summary from a grouped response.
 * Returns undefined if the grouped data or the key is missing.
 */
export function pickSummary(
  grouped: GroupedSummaryDto | undefined,
  resourceType: string
): ResourceSummaryDto | undefined {
  const summaries = grouped?.summaries;
  if (!summaries) return undefined;

  const getSummaryByResourceType = (candidate: string | undefined): ResourceSummaryDto | undefined => {
    if (!candidate) return undefined;
    if (summaries[candidate]) return summaries[candidate];

    const matchingKey = Object.keys(summaries).find(
      (existingResourceType) => existingResourceType.toLowerCase() === candidate.toLowerCase(),
    );

    return matchingKey ? summaries[matchingKey] : undefined;
  };

  const aliases: Record<string, string> = {
    ActivityPerDay: 'UserActivity',
    MeetingParticipants: 'UserActivity',
    MeetingRooms: 'RoomUsage',
    TeamsGroupsAdoption: 'TeamsAdoption',
    TeamsHealth: 'TeamHealth',
    TeamHealth: 'TeamsHealth',
    SuspiciousActivity: 'SuspiciousAccountEvents',
    SuspiciousAccountEvents: 'SuspiciousActivity',
    SecureScores: 'SecureScore',
    SecureScore: 'SecureScores',
    LoginAttemptTypes: 'LoginAttemptType',
    LoginAttemptType: 'LoginAttemptTypes',
    CountryLoginAttempts: 'CountryLoginAttempt',
    CountryLoginAttempt: 'CountryLoginAttempts',
    MailboxDelegationAccesses: 'MailboxDelegationAccess',
    MailboxDelegationAccess: 'MailboxDelegationAccesses',
    GroupActivity: 'GroupsActivity',
    GroupsActivity: 'GroupActivity',
  };

  return getSummaryByResourceType(resourceType) ?? getSummaryByResourceType(aliases[resourceType]);
}

export { useDualSecurityData } from './useDualSecurityData';
export { useDualAdoptionData } from './useDualAdoptionData';
export { useDualUtilisationData } from './useDualUtilisationData';
export { useDualProductivityData } from './useDualProductivityData';
